import React, { forwardRef, useContext, useRef, useState, useEffect, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { border } from 'styled-system';
import { Box } from '../../primitives/Box/Box.es.js';
import '../../utils/assertions.es.js';
import { mergeRefs } from '../../utils/refs.es.js';
import '../../utils/responsive.es.js';
import '../../utils/scrollIntoView.es.js';
import { themeMode, palette, radius } from '../../utils/theme.es.js';
import '../../utils/useEnhancedEffect.es.js';
import 'react-is';
import { Cell } from './TableCell.es.js';
import { TableContext } from './TableContext.es.js';
import { useTableRowEventListener } from './Table.hooks.es.js';

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const Row = styled(Box)`
    display: table-row;
    color: inherit;
    outline: 0;
    vertical-align: middle;

    ${({ hasFocus, isFocus }) => !!hasFocus && !!isFocus && css`
            ${Cell} {
                position: relative;

                &::after {
                    content: ' ';
                    width: 100%;
                    height: 100%;
                    position: absolute;
                    top: 0;
                    left: 0;
                    z-index: -1;
                    background-color: ${themeMode({
  light: css`
                            ${palette("neutral.100")};
                        `,
  dark: css`
                            ${palette("neutral.900")};
                        `
})};
                }

                &:first-of-type::after {
                    width: calc(100% + 12px);
                    right: 0;
                    left: auto;
                    border-top-left-radius: ${radius("base")};
                    border-bottom-left-radius: ${radius("base")};
                }

                &:last-of-type::after {
                    width: calc(100% + 12px);
                    border-top-right-radius: ${radius("base")};
                    border-bottom-right-radius: ${radius("base")};
                }
            }
        `};

    ${({ isRuled }) => isRuled && css`
            border-bottom: ${themeMode({
  light: css`
                    1px solid ${palette("neutral.200")};
                `,
  dark: css`
                    1px solid ${palette("neutral.800")};
                `
})};
        `};

    ${({ hasHover }) => !!hasHover && css`
            ${Cell} {
                position: relative;

                &::after {
                    content: ' ';
                    width: 100%;
                    height: 100%;
                    position: absolute;
                    top: 0;
                    left: 0;
                    z-index: -1;
                }

                &:first-of-type::after {
                    width: calc(100% + 12px);
                    right: 0;
                    left: auto;
                    border-top-left-radius: ${radius("base")};
                    border-bottom-left-radius: ${radius("base")};
                }

                &:last-of-type::after {
                    width: calc(100% + 12px);
                    border-top-right-radius: ${radius("base")};
                    border-bottom-right-radius: ${radius("base")};
                }
            }

            &:hover > ${Cell} {
                &::after {
                    background-color: ${themeMode({
  light: css`
                            ${palette("neutral.100")};
                        `,
  dark: css`
                            ${palette("neutral.900")};
                        `
})};
                }
            }
        `};

    ${({ isSelected }) => !!isSelected && css`
            ${Cell} {
                position: relative;
                &::after {
                    content: ' ';
                    width: 100%;
                    height: 100%;
                    position: absolute;
                    top: 0;
                    left: 0;
                    z-index: -1;
                    background-color: ${themeMode({
  light: css`
                            ${palette("neutral.100")};
                        `,
  dark: css`
                            ${palette("neutral.900")};
                        `
})};
                }
                &:first-of-type::after {
                    width: calc(100% + 12px);
                    right: 0;
                    left: auto;
                    border-radius: 0;
                }

                &:last-of-type::after {
                    width: calc(100% + 12px);
                    border-radius: 0;
                }
            }
        `};

    /* System Props */
    ${border}
`;
const TableRow = forwardRef((_a, forwardedRef) => {
  var _b = _a, { children, rowIndex = -1, tabIndex = -1 } = _b, props = __objRest(_b, ["children", "rowIndex", "tabIndex"]);
  const { isRuled, dispatchRowChangedEvent, focusedRowIndexRef, onRowChange } = useContext(TableContext);
  const rowRef = useRef(null);
  const [isFocus, setIsFocus] = useState(false);
  const ref = mergeRefs(forwardedRef, rowRef);
  useTableRowEventListener(rowIndex, ref, isFocus, setIsFocus);
  useEffect(() => {
    if (focusedRowIndexRef && focusedRowIndexRef.current === rowIndex) {
      setIsFocus(true);
    }
  }, [focusedRowIndexRef, rowIndex]);
  const handleOnClick = useCallback((event) => {
    dispatchRowChangedEvent(rowIndex, false);
    onRowChange && onRowChange(rowIndex, event);
  }, [dispatchRowChangedEvent, onRowChange, rowIndex]);
  return /* @__PURE__ */ React.createElement(Row, __spreadValues({
    onClick: handleOnClick,
    ref,
    isRuled,
    isFocus,
    tabIndex
  }, props), children);
});

export { TableRow };
//# sourceMappingURL=TableRow.es.js.map
